import { Checkbox, Text } from "@fluentui/react-components";
import { translate } from "../config/translator";
import LanguageSelector from "@/components/languageSelector/LanguageSelector";
import PspTextInput from "@/components/pspTextInput/pspTextInput";
import PspButton from "@/components/pspButton/PspButton";
import { FullScreenSpinner } from "@/components/fullScreenSpinner/FullScreenSpinner";
import TextButton from "@/components/textButton/TextButton";
import PspLogo from "@/components/pspLogo/PspLogo";
import { useLogin } from "./login/useLogin";
import { CookiesAndPrivacyLinks } from "@/components/cookiesAndPrivacyLinks/cookiesAndPrivacyLinks";
import { Link } from "react-router-dom";
import Box from "@/components/box/box";
import { MfaMethodItem } from "@/components/mfaItem/mfaMethodItem";
import { clsx } from "clsx/lite";
// eslint-disable-next-line max-len
import { PasswordRequirementsList } from "@/components/passwordRequirementsList/passwordRequirementsList";

export default function Login() {
  const {
    onBack,
    onKeyDownPassword,
    onKeyDownEmail,
    isLoadingLogin,
    loginStage,
    email,
    password,
    lang,
    navigate,
    setEmail,
    setPassword,
    onContinue,
    showInvalidCredentialsMessage,
    passwordInputRef,
    isTablet,
    mfaMethods,
    setSelectedMfaMethod,
    selectedMfaMethod,
    onChangeTotpCode,
    onChangeEmailCode,
    totpCode,
    emailCode,
    sendEmailCode,
    resendEmailCodeCooldownSeconds,
    emailCodeWarning,
    totpCodeWarning,
    emailCodeInputRef,
    totpInputRef,
    trustDevice,
    setTrustDevice,
    onKeyDownMfa,
    newPassword,
    newPasswordConfirm,
    setNewPasswordConfirm,
    passwordRequirements,
    canChangePassword,
    passwordsDontMatch,
    onChangeNewPassword,
    newPasswordConfirmInputRef,
    onKeyDownNewPassword,
    onKeyDownNewPasswordConfirm,
  } = useLogin();

  if (isLoadingLogin) {
    return (
      <div className="bg-canvas-primary tablet:bg-white fixed inset-0">
        <FullScreenSpinner />
      </div>
    );
  }

  return (
    <div className="w-screen h-screen flex flex-col justify-center items-center">
      {/* LOGO */}
      <div className="absolute top-16 tablet:top-20 w-full flex flex-row justify-center">
        <PspLogo />
      </div>

      {/* LANGUAGE SELECTOR */}
      <div
        className={`
          absolute
          bottom-6
          left-6
          tablet:bottom-auto
          tablet:left-auto
          tablet:top-10
          tablet:right-10
          flex
          tablet:justify-end
        `}
      >
        <LanguageSelector />
      </div>

      <Box
        className="w-min"
        title={translate("login", lang)}
        secondaryText={
          loginStage === "email"
            ? translate("enterEmailToContinue", lang)
            : loginStage === "passwordExpired"
              ? translate("yourPasswordIsExpiredMsg", lang)
              : loginStage === "password"
                ? translate("pleaseEnterPassword", lang)
                : loginStage === "select2faMethod"
                  ? translate("selectOptionToContinue", lang)
                  : loginStage === "inputAuthenticatorCode" && selectedMfaMethod
                    ? translate("enterTotpOf", lang, {
                        "{authenticatorLabel}": selectedMfaMethod?.label,
                      })
                    : loginStage === "inputEmailCode" && selectedMfaMethod
                      ? translate("weSentEmailCodeTo", lang, {
                          "{email}": email,
                        })
                      : undefined
        }
      >
        <div className="w-60 tablet:w-100 flex-col flex gap-8">
          <div className="flex flex-col gap-4">
            {loginStage === "email" || loginStage === "password" ? (
              <>
                {/* EMAIL */}
                <PspTextInput
                  onKeyDown={onKeyDownEmail}
                  autoFocus
                  onChange={setEmail}
                  value={email}
                  disabled={loginStage !== "email"}
                  label="email"
                />

                {/* PASSWORD */}
                {loginStage === "password" && (
                  <PspTextInput
                    autoFocus
                    onKeyDown={onKeyDownPassword}
                    onChange={setPassword}
                    value={password}
                    label="password"
                    type="password"
                    ref={passwordInputRef}
                  />
                )}

                {showInvalidCredentialsMessage && (
                  <div className="relative w-full flex flex-row justify-center">
                    <Text className="text-warning">
                      {translate("invalidCredentials", lang)}
                    </Text>
                  </div>
                )}
              </>
            ) : loginStage === "select2faMethod" ? (
              <div className="flex flex-col gap-4">
                {mfaMethods?.map((method) => (
                  <MfaMethodItem
                    // primary is hidden for now
                    // primary={method.isPrimary}
                    type={method.mfaType}
                    key={`mfa-method-${method.id}`}
                    label={method.label}
                    onClick={() => setSelectedMfaMethod(method)}
                    selected={method.id === selectedMfaMethod?.id}
                  />
                ))}
              </div>
            ) : loginStage === "inputAuthenticatorCode" ? (
              <div className="flex flex-col gap-4">
                <PspTextInput
                  ref={totpInputRef}
                  invalidDataMessage={
                    totpCodeWarning === "wrong"
                      ? "theCodeIsIncorrect"
                      : totpCodeWarning === "tooShort"
                        ? "theCodeIsTooShort"
                        : totpCodeWarning === "tooLong"
                          ? "theCodeIsTooLong"
                          : undefined
                  }
                  onKeyDown={onKeyDownMfa}
                  autoFocus
                  label="totpCode"
                  value={totpCode}
                  disableAutocomplete
                  onChange={onChangeTotpCode}
                  placeholder="totpPlaceholder"
                />
                <Checkbox
                  onChange={(_, data) =>
                    setTrustDevice(
                      data.checked === "mixed" ? true : data.checked,
                    )
                  }
                  checked={trustDevice}
                  label={translate("trustThisDevice", lang)}
                />
              </div>
            ) : loginStage === "inputEmailCode" ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2 items-start">
                  <PspTextInput
                    ref={emailCodeInputRef}
                    invalidDataMessage={
                      emailCodeWarning === "wrong"
                        ? "theCodeIsIncorrect"
                        : emailCodeWarning === "tooShort"
                          ? "theCodeIsTooShort"
                          : emailCodeWarning === "tooLong"
                            ? "theCodeIsTooLong"
                            : undefined
                    }
                    onKeyDown={onKeyDownMfa}
                    autoFocus
                    value={emailCode}
                    disableAutocomplete
                    onChange={onChangeEmailCode}
                    label="emailCode"
                    placeholder="totpPlaceholder"
                  />
                  <button
                    disabled={resendEmailCodeCooldownSeconds !== null}
                    className={`underline ${
                      resendEmailCodeCooldownSeconds === null
                        ? "cursor-pointer text-font-secondary"
                        : "text-gray-400"
                    }`}
                    onClick={() =>
                      selectedMfaMethod !== null &&
                      selectedMfaMethod.mfaType === "email"
                        ? sendEmailCode(selectedMfaMethod.id)
                        : undefined
                    }
                  >
                    {`${translate("sendCodeAgain", lang)}${
                      resendEmailCodeCooldownSeconds
                        ? ` (${resendEmailCodeCooldownSeconds})`
                        : ""
                    }`}
                  </button>
                </div>
                <Checkbox
                  onChange={(_, data) =>
                    setTrustDevice(
                      data.checked === "mixed" ? true : data.checked,
                    )
                  }
                  checked={trustDevice}
                  label={translate("trustThisDevice", lang)}
                />
              </div>
            ) : (
              loginStage === "passwordExpired" && (
                <div className="flex flex-col gap-4">
                  <PspTextInput
                    type="password"
                    labelString={translate("newPassword", lang)}
                    disableAutocomplete
                    value={newPassword}
                    onChange={onChangeNewPassword}
                    onKeyDown={onKeyDownNewPassword}
                  />
                  <PspTextInput
                    type="password"
                    labelString={translate("confirmNewPassword", lang)}
                    disableAutocomplete
                    value={newPasswordConfirm}
                    onChange={setNewPasswordConfirm}
                    ref={newPasswordConfirmInputRef}
                    onKeyDown={onKeyDownNewPasswordConfirm}
                  />
                  <PasswordRequirementsList
                    passwordRequirements={passwordRequirements}
                    passwordsDontMatch={passwordsDontMatch}
                  />
                </div>
              )
            )}
          </div>

          <div className="w-full flex flex-row justify-end gap-4">
            {/* BACK BUTTON */}
            {loginStage !== "email" && (
              <PspButton appearance="secondary" onClick={onBack}>
                {translate("back", lang)}
              </PspButton>
            )}

            {/* SIGN IN / CONTINUE BUTTON */}
            <PspButton
              appearance="primary"
              onClick={onContinue}
              disabled={
                (loginStage === "email" && email.length === 0) ||
                (loginStage === "password" && password.length === 0) ||
                (loginStage === "select2faMethod" &&
                  selectedMfaMethod === null) ||
                (loginStage === "inputAuthenticatorCode" &&
                  totpCode.replaceAll(" ", "").length !== 6) ||
                (loginStage === "inputEmailCode" &&
                  emailCode.replaceAll(" ", "").length !== 6) ||
                (loginStage === "passwordExpired" && !canChangePassword) ||
                isLoadingLogin
              }
            >
              {loginStage === "email" || loginStage === "select2faMethod"
                ? translate("continue", lang)
                : translate("signIn", lang)}
            </PspButton>
          </div>

          {/* RESET PASSWORD BUTTON */}
          {loginStage === "password" && (
            <div
              className={clsx(
                "flex",
                "flex-row",
                "justify-center",
                "absolute",
                "bottom-[max(calc(50%-16rem),8rem)]",
                "left-0",
                "right-0",
              )}
            >
              <TextButton
                onClick={() =>
                  navigate("/reset-password", { state: { email } })
                }
              >
                {translate("resetPassword", lang)}
              </TextButton>
            </div>
          )}

          {/* COOKIES AND PRIVACY POLICY LINK */}
          <div
            className={clsx(
              "absolute",
              "bottom-4",
              "tablet:bottom-8",
              "right-4",
              "tablet:right-1/2",
              "tablet:translate-x-1/2",
              "flex",
              "flex-col",
              "items-end",
              "gap-1",
            )}
          >
            <CookiesAndPrivacyLinks />
            {!isTablet && (
              <Link to="/help" className="underline text-primary">
                {translate("needHelp", lang)}
              </Link>
            )}
          </div>

          {/* CONTACT PAGE LINK */}
          {isTablet && (
            <div className="absolute bottom-8 right-8">
              <Link to="/help" className="underline text-primary">
                {translate("needHelp", lang)}
              </Link>
            </div>
          )}
        </div>
      </Box>
    </div>
  );
}
